import {AsyncResult, fromResult, Loaded, Loading, ServerError} from 'Core/models/Async';
import {fold, isRight, map} from 'fp-ts/lib/Either';
import {all, call, put, select, takeLatest} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import A360CommissionReportAPI from '../api/a360CommissionsReportApi';
import {Producer} from '../components/A360Commissions/types';
import {
  A360CommissionReportingActionType,
  A360CommissionReportingState,
  changeAvailableProducerCodesForReporting,
  changeCommissionsReportData,
  changeIsProducerFilterLoading,
  changeSelectedProducerCodesForReporting,
  CommissionsReportData,
  getCommissionsReportData,
  getAvailableProducerCodesForReport,
} from '../ducks/a360CommissionsReport';
import {pipe} from 'fp-ts/lib/pipeable';

function* fetchProducerCodesForCommissionReport(action: ReturnType<typeof getAvailableProducerCodesForReport>) {
  yield put(changeIsProducerFilterLoading(true));
  try {
    const producerCodesResponse: AsyncResult<Producer[]> = yield call(
      A360CommissionReportAPI.fetchProducerCodesFilterData,
      action.payload.isNewYork
    );

    if (isRight(producerCodesResponse)) {
      yield all([
        put(changeAvailableProducerCodesForReporting(new Loaded(producerCodesResponse.right))),
        put(
          changeSelectedProducerCodesForReporting(
            producerCodesResponse.right?.length > 0
              ? [`${producerCodesResponse.right[0]?.agentNumber} - ${producerCodesResponse.right[0]?.agentName}`]
              : []
          )
        ),
      ]);
    }
  } catch (error) {
    console.error(error);
    yield put(changeAvailableProducerCodesForReporting(new ServerError('Failed to fetch producer codes')));
  } finally {
    yield put(changeIsProducerFilterLoading(false));
  }
}

function* producerCodesForCommissionsReportSaga() {
  yield takeLatest(
    A360CommissionReportingActionType.GET_AVAILABLE_PRODUCER_CODES_FOR_REPORTING,
    fetchProducerCodesForCommissionReport
  );
}

function* fetchCommissionReport(action: ReturnType<typeof getCommissionsReportData>) {
  const {filter}: A360CommissionReportingState = yield select((state: ReducerState) => state.a360CommissionsReport);
  yield put(changeCommissionsReportData(new Loading()));

  const response: AsyncResult<CommissionsReportData> = yield call(
    A360CommissionReportAPI.fetchCommissionsReport,
    filter.selectedProducerCodes,
    filter.selectedDate,
    filter.selectedReport
  );

  yield pipe(
    response,
    map(fromResult),
    fold(
      _error => put(changeCommissionsReportData(new ServerError('Could not fetch commission report data.'))),
      value => put(changeCommissionsReportData(value))
    )
  );
}

function* commissionReportSaga() {
  yield takeLatest(A360CommissionReportingActionType.GET_COMMISSIONS_REPORT_DATA, fetchCommissionReport);
}

export default function*() {
  yield all([producerCodesForCommissionsReportSaga(), commissionReportSaga()]);
}
